import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Modal,
  TextField,
  IndexTable,
  Text,
  InlineStack,
  BlockStack,
  useIndexResourceState,
} from "@shopify/polaris";
import {
  COUNTRY_OPTIONS,
  flagEmojiForCountryCode,
} from "../../settings/countryLanguageOptions";

function CountryPickerTable({ countries, initialSelected, onDraftChange }) {
  const { selectedResources, handleSelectionChange } = useIndexResourceState(countries, {
      resourceIDResolver: (country) => country.value,
      selectedResources: initialSelected,
    });

  useEffect(() => {
    onDraftChange(selectedResources);
  }, [onDraftChange, selectedResources]);

  const rowMarkup = countries.map((country, index) => {
    const flag = flagEmojiForCountryCode(country.value);
    return (
      <IndexTable.Row
        id={country.value}
        key={country.value}
        position={index}
        selected={selectedResources.includes(country.value)}
      >
        <IndexTable.Cell>
          <InlineStack gap="300" blockAlign="center" wrap={false}>
            {flag ? (
              <Text as="span" variant="headingLg">
                {flag}
              </Text>
            ) : null}
            <Text as="span" variant="bodyMd">
              {country.label}
            </Text>
          </InlineStack>
        </IndexTable.Cell>
      </IndexTable.Row>
    );
  });

  return (
    <div className="fsb-country-picker-modal__table">
      <IndexTable
        resourceName={{ singular: "country", plural: "countries" }}
        itemCount={countries.length}
        selectedItemsCount={selectedResources.length}
        onSelectionChange={handleSelectionChange}
        headings={[{ title: "Country" }]}
        selectable
      >
        {rowMarkup}
      </IndexTable>
    </div>
  );
}

export default function CountryPickerModal({ open, onClose, selected = [], onSelect }) {
  const [search, setSearch] = useState("");
  const [draftSelected, setDraftSelected] = useState(selected);

  useEffect(() => {
    if (open) {
      setSearch("");
      setDraftSelected(selected || []);
    }
  }, [open, selected]);

  const filteredCountries = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return COUNTRY_OPTIONS;
    return COUNTRY_OPTIONS.filter(
      (c) => c.value.toLowerCase().includes(q) || c.label.toLowerCase().includes(q),
    );
  }, [search]);

  const handleDraftChange = useCallback((next) => {
    setDraftSelected(next);
  }, []);

  const handleSelect = useCallback(() => {
    onSelect?.(draftSelected);
    onClose?.();
  }, [draftSelected, onClose, onSelect]);

  const tableKey = open ? (selected || []).join(",") : "closed";

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Country selection"
      size="small"
      primaryAction={{ content: "Select", onAction: handleSelect }}
      secondaryActions={[{ content: "Cancel", onAction: onClose }]}
    >
      <Modal.Section>
        <BlockStack gap="300">
          <TextField
            label="Search countries"
            labelHidden
            placeholder="Search countries"
            value={search}
            onChange={setSearch}
            autoComplete="off"
            clearButton
            onClearButtonClick={() => setSearch("")}
          />
          {open ? (
            <CountryPickerTable
              key={tableKey}
              countries={filteredCountries}
              initialSelected={selected || []}
              onDraftChange={handleDraftChange}
            />
          ) : null}
        </BlockStack>
      </Modal.Section>
    </Modal>
  );
}
